import { useState } from 'react';
import { repositoryService } from '../services/repositoryService';
import { eiapClusterService } from '../services/eiapClusterService';
import { cnisService } from '../services/cnisService';
import { enmService } from '../services/enmService';
import { productService } from '../services/productService';
import { SoftwareRepository, EiapCluster, Enm, ProductLine } from '../types/automation-flow.types';
import { CnisEndpoint } from '../components/environments/CNIS/cnis-endpoint-interfaces';
import { MoshellEndpoint, PBNodesDeploy } from '../components/environments/Moshell/moshell-endpoint-interfaces';
import { moshellService } from '../services/moshellService';

export function useRepositories() {
  const [repositories, setRepositories] = useState<SoftwareRepository[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchRepositories = async () => {
    try {
      const resp = await repositoryService.getAll();
      setRepositories(resp.data || []);
      setError(null);
      return resp.data || [];
    } catch (err) {
      setRepositories([]);
      setError(err as Error);
      throw err;
    }
  };

  return { repositories, error, fetchRepositories };
}

export function useEiapClusters() {
  const [eiapClusters, setEiapClusters] = useState<EiapCluster[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchEiapClusters = async () => {
    try {
      const resp = await eiapClusterService.getAll();
      setEiapClusters(resp.data || []);
      setError(null);
      return resp.data || [];
    } catch (err) {
      setEiapClusters([]);
      setError(err as Error);
      throw err;
    }
  };

  return { eiapClusters, error, fetchEiapClusters };
}

export function useEnms() {
  const [enms, setEnms] = useState<Enm[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchEnms = async () => {
    try {
      const resp = await enmService.getAll();
      setEnms(resp.data || []);
      setError(null);
      return resp.data || [];
    } catch (err) {
      setEnms([]);
      setError(err as Error);
      throw err;
    }
  };

  return { enms, error, fetchEnms };
}

export function useMoshells() {
  const [moshells, setMoshells] = useState<MoshellEndpoint[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchMoshells = async () => {
    try {
      const resp = await moshellService.getAll();
      setMoshells(resp.data || []);
      setError(null);
      return resp.data || [];
    } catch (err) {
      setMoshells([]);
      setError(err as Error);
      throw err;
    }
  };

  return { moshells, error, fetchMoshells };
}

export function useMoshellNodes() {
  const [moshellNodes, setMoshellNodes] = useState<PBNodesDeploy[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchMoshellNodes = async (id: string) => {
    try {
      const resp = await moshellService.getNodes(id);
      setMoshellNodes(resp.data || []);
      setError(null);
      return resp.data || [];
    } catch (err) {
      setMoshellNodes([]);
      setError(err as Error);
      throw err;
    }
  };

  return { moshellNodes, error, fetchMoshellNodes };
}

export function useProductLines() {
  const [productLines, setProductLines] = useState<ProductLine[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchProductLines = async (productTypeName: string) => {
    try {
      const resp = await productService.getByProductType(productTypeName);
      setProductLines(resp.data || []);
      setError(null);
      return resp.data || [];
    } catch (err) {
      setProductLines([]);
      setError(err as Error);
      throw err;
    }
  };

  return { productLines, error, fetchProductLines };
}

export function useCnis() {
  const [cnis, setCnis] = useState<CnisEndpoint[]>([]);
  const [error, setError] = useState<Error | null>(null);

  const fetchCnis = async () => {
    try {
      const res = await cnisService.getAll();
      setCnis(res.data || []);
      setError(null);
      return res.data || [];
    } catch (err) {
      setCnis([]);
      setError(err as Error);
      throw err;
    }
  };

  return { cnis, error, fetchCnis };
}
